import React, { useContext, useMemo, useCallback, useEffect } from 'react';
import _, { get } from 'underscore';
import PropTypes from 'prop-types';
import { I18nContext } from '@bingads-webui-react/i18n-context';
import { InstrumentationContext } from '@bingads-webui-react/hoc-with-instrumentation';
import { getAutoCroppedImages, UnifiedPreviewCarousel, PreviewCarousel, getDefaultCurrentSelectedImageId, getImageListWithoutLogo } from '@bingads-webui-campaign-react/image-experience';

import { getUnifiedAdPreviewConfig } from './get-unified-ad-preview-config';

const findImageSetById = (imageList, baseImageId) => {
  if (!baseImageId) return false;

  const imageSetToEdit = _.find(imageList, placements => placements[0].BaseId.toString() === baseImageId.toString());
  return imageSetToEdit;
};

const setImageSetById = (imageList, baseImageId, newImageSet) => {
  if (!baseImageId) return false;

  const imageSetIdx = _.findIndex(imageList, placements =>
    placements[0].BaseId.toString() === baseImageId.toString());

  if (imageSetIdx >= 0) {
    const newImageList = [...imageList];
    newImageList.splice(imageSetIdx, 1, newImageSet);
    return newImageList;
  }
  return [...imageList, newImageSet];
};

export const SearchMMAAdPreview = ({
  data,
  onDataChange,
  permissions,
  genericImage,
  previewTexts,
  previewImagesSharedState,
  setPreviewImagesSharedState,
}) => {
  const i18n = useContext(I18nContext);
  const instrumentation = useContext(InstrumentationContext);
  const enableAdPreviewLibrary = get(permissions.dynamic, 'AdPreviewLibrarySearchMMA', false);
  const imageList = data.Images && data.Images.ImageList;

  const onCropImage = useCallback((cropImageObj) => {
    setPreviewImagesSharedState({ ...previewImagesSharedState, cropImageObj });
    // autorotate=false
  }, [previewImagesSharedState, setPreviewImagesSharedState]);


  const { previewImageId } = previewImagesSharedState;

  useEffect(() => {
    if (!previewImageId && imageList && imageList.length > 0) {
      const firstImageId = getDefaultCurrentSelectedImageId(imageList);
      if (firstImageId) {
        setPreviewImagesSharedState({ ...previewImagesSharedState, previewImageId: firstImageId });
      }
    }
  }, [imageList, previewImageId, previewImagesSharedState, setPreviewImagesSharedState]);

  const onAddAspectRatio = useCallback((imageType) => {
    // We don't support logo in ad preview, so we need to filter out logo images
    const imageListWithoutLogo = getImageListWithoutLogo(imageList);
    const previewImages = findImageSetById(imageListWithoutLogo, previewImageId);

    let newPreviewImages = null;
    const targetedRatioIdx = _.findIndex(previewImages, image => image.type === imageType);
    if (targetedRatioIdx >= 0) {
      const targetedRatio = previewImages[targetedRatioIdx].clone();
      targetedRatio.setIncluded(true);
      newPreviewImages = [...previewImages];
      newPreviewImages.splice(targetedRatioIdx, 1, targetedRatio);
    } else {
      // for image added before partial aspect ratio is supported, may missed some ratio info
      const croppedImages = getAutoCroppedImages({
        originalImageToCrop: _.first(previewImages),
        isMultimediaAd: true,
        isMultimediaAdinMSANEnabled: false,
      });
      const target = _.find(croppedImages, image => image.type === imageType);
      target.isIncluded = true;
      newPreviewImages = [...previewImages, target];
      newPreviewImages.sort((a, b) => b.aspectRatio - a.aspectRatio);
    }

    const newImageList = setImageSetById(imageList, previewImageId, newPreviewImages);
    onDataChange({
      data: {
        ...data,
        Images: {
          ...data.Images,
          ImageList: newImageList,
        },
      },
    });
    return newImageList;
  }, [data, imageList, onDataChange, previewImageId]);

  const onRemoveAspectRatio = (imageType) => {
    const imageListWithoutLogo = getImageListWithoutLogo(imageList);
    const previewImages = findImageSetById(imageListWithoutLogo, previewImageId);

    const targetedRatioIdx = _.findIndex(previewImages, image => image.type === imageType);
    if (targetedRatioIdx >= 0) {
      const targetedRatio = previewImages[targetedRatioIdx].clone();
      targetedRatio.setIncluded(false);
      const newPreviewImages = [...previewImages];
      newPreviewImages.splice(targetedRatioIdx, 1, targetedRatio);
      const newImageList = setImageSetById(imageList, previewImageId, newPreviewImages);
      onDataChange({
        data: {
          ...data,
          Images: {
            ...data.Images,
            ImageList: newImageList,
          },
        },
      });
      return newImageList;
    }
    return imageList;
  };

  const PreviewCarouselComponent = enableAdPreviewLibrary ? UnifiedPreviewCarousel : PreviewCarousel;
  const adPreviewConfig = getUnifiedAdPreviewConfig({ isMultimediaAd: true });

  const imagesForPreview = useMemo(() => {
    const imageListWithoutLogo = getImageListWithoutLogo(imageList);

    if (previewImageId) {
      const previewImages = findImageSetById(imageListWithoutLogo, previewImageId);

      if (previewImages) {
        return previewImages;
      }
    }

    if (imageListWithoutLogo.length > 0) {
      return _.first(imageListWithoutLogo);
    }

    return [];
  }, [imageList, previewImageId]);

  return (
    <PreviewCarouselComponent
      imageList={imagesForPreview}
      previewTexts={previewTexts}
      enableAutoRotate
      i18n={i18n}
      permissions={permissions}
      genericImage={genericImage}
      isMultimediaAd
      onCropImage={onCropImage}
      onAddAspectRatio={onAddAspectRatio}
      onRemoveAspectRatio={onRemoveAspectRatio}
      isAdCreatorEnabled
      instrumentation={instrumentation}
      totalImageListCount={imageList && imageList.length}
      isImproveMMA={false}
      newI18n={i18n}
      adPreviewConfig={adPreviewConfig}
    />
  );
};

SearchMMAAdPreview.propTypes = {
  data: PropTypes.shape({
    FinalUrls: PropTypes.string,
    MobileFinalUrls: PropTypes.string,
    BusinessName: PropTypes.string,
    Headlines: PropTypes.arrayOf(PropTypes.string),
    LongHeadlines: PropTypes.arrayOf(PropTypes.string),
    Descriptions: PropTypes.arrayOf(PropTypes.string),
    Images: PropTypes.shape({
      ImageList: PropTypes.arrayOf(PropTypes.shape({})),
    }),
    TrackingUrlTemplate: PropTypes.string,
    FinalUrlSuffix: PropTypes.string,
    UrlCustomParameters: PropTypes.string,
    FinalUrlForRecommendation: PropTypes.string,
    CallToAction: PropTypes.shape({
      cta: PropTypes.string,
      language: PropTypes.string,
    }),
  }).isRequired,
  onDataChange: PropTypes.func.isRequired,
  permissions: PropTypes.shape({
    dynamic: PropTypes.shape({
      AdPreviewLibrarySearchMMA: PropTypes.bool,
    }),
  }).isRequired,
  genericImage: PropTypes.string.isRequired,
  previewTexts: PropTypes.shape({
    shortHeadline: PropTypes.string,
    longHeadline: PropTypes.string,
    description: PropTypes.string,
    businessName: PropTypes.string,
    displayUrl: PropTypes.string,
    url: PropTypes.string,
    callToAction: PropTypes.string,
  }).isRequired,
  previewImagesSharedState: PropTypes.shape({
    previewImageId: PropTypes.string,
    cropImageObj: PropTypes.shape({}),
  }).isRequired,
  setPreviewImagesSharedState: PropTypes.func.isRequired,
};
